import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './services.css';

// Reusable animated component
function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function Services() {
  const [activeService, setActiveService] = useState(0);

  // Industries data for Sri Lankan market
  const industries = [
    { name: "Healthcare", icon: "🏥", description: "Hospitals, clinics, and healthcare providers" },
    { name: "Retail & E-commerce", icon: "🛒", description: "Retail stores, supermarkets, and online shopping platforms" },
    { name: "Education", icon: "🎓", description: "Schools, universities, and educational institutions" },
    { name: "Manufacturing", icon: "🏭", description: "Garment, ceramic, and various manufacturing industries" },
    { name: "Beauty Salon & Wellness", icon: "💅", description: "Spas, beauty parlors, and wellness centers" },
    { name: "Agriculture & Plantations", icon: "🌱", description: "Tea, rubber, coconut, and other agricultural sectors" },
    { name: "Tourism & Hospitality", icon: "🏨", description: "Hotels, travel agencies, and tourism services" },
    { name: "Export & Trade", icon: "📦", description: "Export companies and international trade businesses" }
  ];

  // Services data
  const services = [
    {
      id: 1,
      title: "Custom Software Development",
      icon: "💻",
      description: "Tailor-made software solutions designed to address your specific business challenges and drive digital transformation.",
      features: [
        "Web Application Development",
        "Desktop Software Solutions",
        "API Development & Integration",
        "Legacy System Modernization",
        "Quality Assurance & Testing"
      ],
      benefits: [
        "Increased operational efficiency",
        "Competitive advantage",
        "Scalable architecture",
        "Seamless integration with existing systems"
      ]
    },
    {
      id: 2,
      title: "Cloud Solutions & Migration",
      icon: "☁️",
      description: "End-to-end cloud services from strategy to implementation, ensuring scalability, security, and cost-efficiency.",
      features: [
        "Cloud Infrastructure Setup",
        "Migration Services",
        "Cloud Security & Compliance",
        "DevOps & Automation",
        "24/7 Monitoring & Support"
      ],
      benefits: [
        "Reduced IT infrastructure costs",
        "Enhanced scalability and flexibility",
        "Improved disaster recovery",
        "Access to latest technologies"
      ]
    },
    {
      id: 3,
      title: "AI & Machine Learning",
      icon: "🤖",
      description: "Intelligent solutions that leverage artificial intelligence to automate processes, gain insights, and drive innovation.",
      features: [
        "Predictive Analytics",
        "Natural Language Processing",
        "Computer Vision Solutions",
        "Recommendation Engines",
        "AI Strategy Consulting"
      ],
      benefits: [
        "Data-driven decision making",
        "Process automation",
        "Enhanced customer experiences",
        "Competitive intelligence"
      ]
    },
    {
      id: 4,
      title: "Cybersecurity Services",
      icon: "🔒",
      description: "Comprehensive security solutions to protect your digital assets, data, and infrastructure from evolving threats.",
      features: [
        "Security Assessment & Auditing",
        "Threat Detection & Response",
        "Data Encryption & Protection",
        "Compliance Management",
        "Security Awareness Training"
      ],
      benefits: [
        "Protection against cyber threats",
        "Regulatory compliance",
        "Customer trust and confidence",
        "Reduced risk of data breaches"
      ]
    },
    {
      id: 5,
      title: "IT Consulting",
      icon: "📊",
      description: "Strategic guidance to optimize your technology infrastructure, processes, and digital transformation journey.",
      features: [
        "Technology Strategy",
        "Digital Transformation",
        "IT Infrastructure Optimization",
        "Process Improvement",
        "Vendor Management"
      ],
      benefits: [
        "Expert technology guidance",
        "Cost optimization",
        "Future-proof solutions",
        "Alignment with business goals"
      ]
    },
    {
      id: 6,
      title: "Mobile App Development",
      icon: "📱",
      description: "Native and cross-platform mobile applications that deliver exceptional user experiences across all devices.",
      features: [
        "iOS & Android Development",
        "Cross-Platform Solutions",
        "UI/UX Design",
        "App Store Optimization",
        "Maintenance & Updates"
      ],
      benefits: [
        "Enhanced customer engagement",
        "Mobile workforce enablement",
        "New revenue streams",
        "Brand presence on mobile"
      ]
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: "Discovery & Analysis",
      description: "We begin by thoroughly understanding your business objectives, challenges, and requirements."
    },
    {
      step: "02",
      title: "Planning & Strategy",
      description: "Our experts develop a comprehensive strategy tailored to your specific needs and goals."
    },
    {
      step: "03",
      title: "Design & Development",
      description: "We create innovative solutions using cutting-edge technologies and best practices."
    },
    {
      step: "04",
      title: "Testing & Quality Assurance",
      description: "Rigorous testing ensures your solution meets the highest standards of quality and performance."
    },
    {
      step: "05",
      title: "Deployment & Launch",
      description: "We seamlessly implement your solution with minimal disruption to your operations."
    },
    {
      step: "06",
      title: "Support & Maintenance",
      description: "Ongoing support and optimization to ensure your solution continues to deliver value."
    }
  ];

  return (
    <div className="services-container">
      {/* ===== HERO SECTION ===== */}
      <section className="services-hero">
        <div className="hero-background">
          <div className="hero-pattern"></div>
        </div>
        <motion.div 
          className="services-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-badge">
            <span>Expert Solutions</span>
          </div>
          <h1>Comprehensive <span className="gradient-text">IT Services</span></h1>
          <p className="hero-subtitle">
            Leverage our expertise to transform your business with cutting-edge technology solutions 
            designed for innovation, scalability, and sustainable growth.
          </p>
          <div className="hero-stats">
            <div className="stat">
              <span className="stat-number">50+</span>
              <span className="stat-label">Technologies</span>
            </div>
            <div className="stat">
              <span className="stat-number">15+</span>
              <span className="stat-label">Years Experience</span>
            </div>
            <div className="stat">
              <span className="stat-number">99%</span>
              <span className="stat-label">Client Retention</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ===== SERVICES GRID ===== */}
      <AnimatedSection className="services-grid-section">
        <div className="section-header">
          <h2>Our Core Services</h2>
          <p>Comprehensive IT solutions tailored to drive your business forward</p>
        </div>
        
        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className={`service-card ${activeService === index ? 'active' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              onClick={() => setActiveService(index)}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <div className="service-features">
                {service.features.slice(0, 3).map((feature, i) => (
                  <span key={i} className="feature-tag">{feature}</span>
                ))}
                {service.features.length > 3 && (
                  <span className="feature-tag">+{service.features.length - 3} more</span>
                )}
              </div>
              <button className="service-cta">Learn More →</button>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>

      {/* ===== SERVICE DETAIL SECTION ===== */}
      <AnimatedSection className="service-detail-section">
        <div className="detail-container">
          <div className="detail-content">
            <h2>{services[activeService].title}</h2>
            <p>{services[activeService].description}</p>
            
            <div className="features-list">
              <h4>Key Features</h4>
              <ul>
                {services[activeService].features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
            
            <div className="benefits-list">
              <h4>Business Benefits</h4>
              <ul>
                {services[activeService].benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>
            
            <motion.button 
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Request Consultation
            </motion.button>
          </div>
          
          <div className="detail-visual">
            <div className="visual-placeholder">
              <div className="floating-elements">
                <div className="element element-1">{services[activeService].icon}</div>
                <div className="element element-2">🚀</div>
                <div className="element element-3">⭐</div>
              </div>
              <div className="main-circle">
                <span>Innovation</span>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ===== INDUSTRIES SECTION ===== */}
      <AnimatedSection className="industries-section">
        <div className="section-header">
          <h2>Industries We Serve in Sri Lanka</h2>
          <p>Tailored solutions for diverse industry challenges and opportunities</p>
        </div>
        
        <div className="industries-grid">
          {industries.map((industry, index) => (
            <motion.div
              key={index}
              className="industry-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="industry-icon">{industry.icon}</div>
              <h4>{industry.name}</h4>
              <p>{industry.description}</p>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>

      {/* ===== PROCESS SECTION ===== */}
      <AnimatedSection className="process-section">
        <div className="section-header">
          <h2>Our Process</h2>
          <p>A structured approach to delivering exceptional results</p>
        </div>
        
        <div className="process-steps">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              className="process-step"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7, delay: index * 0.2 }}
            >
              <div className="step-number">{step.step}</div>
              <div className="step-content">
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </AnimatedSection>

      {/* ===== CTA SECTION ===== */}
      <AnimatedSection className="services-cta">
        <div className="cta-content">
          <h2>Ready to Transform Your Business?</h2>
          <p>Schedule a free consultation with our experts to discuss your project requirements</p>
          <div className="cta-buttons">
            <motion.button 
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Today
            </motion.button>
            <motion.button 
              className="btn btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Case Studies
            </motion.button>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
}

export default Services;